import { Injectable } from "@angular/core";
import { Observable, Subject } from "rxjs";
import { Environment } from "../../environment";
import { AccountService } from "./account.service";

interface BrowserSpeechRecognitionResult {
    readonly isFinal: boolean;
    readonly length: number;
    [index: number]: { transcript: string };
}

interface BrowserSpeechRecognitionResultList {
    readonly length: number;
    [index: number]: BrowserSpeechRecognitionResult;
}

interface BrowserSpeechRecognitionEvent extends Event {
    readonly resultIndex: number;
    readonly results: BrowserSpeechRecognitionResultList;
}

interface BrowserSpeechRecognitionErrorEvent extends Event {
    readonly error: string;
    readonly message?: string;
}

interface BrowserSpeechRecognition extends EventTarget {
    lang: string;
    continuous: boolean;
    interimResults: boolean;
    maxAlternatives: number;
    onresult: ((event: BrowserSpeechRecognitionEvent) => void) | null;
    onerror: ((event: BrowserSpeechRecognitionErrorEvent) => void) | null;
    onend: (() => void) | null;
    start(): void;
    stop(): void;
    abort(): void;
}

interface BrowserSpeechRecognitionConstructor {
    new(): BrowserSpeechRecognition;
}

interface BrowserWindow extends Window {
    SpeechRecognition?: BrowserSpeechRecognitionConstructor;
    webkitSpeechRecognition?: BrowserSpeechRecognitionConstructor;
}

interface ActiveStreamingPlayback {
    audioContext: AudioContext;
    gainNode: GainNode;
    sourceNodes: Set<AudioBufferSourceNode>;
    scheduledTime: number;
    pendingSourceCount: number;
    streamCompleted: boolean;
}

@Injectable({
    providedIn: "root",
})
export class VoiceService {
    private recognition?: BrowserSpeechRecognition;
    private recordingSubject?: Subject<string>;
    private shouldKeepRecording = false;
    private recordingLanguage = "en-US";
    private committedTranscript = "";
    private sessionResults: Array<{ transcript: string; isFinal: boolean }> = [];
    private lastEmittedTranscript = "";
    private restartTimeoutId?: number;
    private activeReadTextResolver?: () => void;
    private activePlaybackAbortController?: AbortController;
    private activeStreamingPlayback?: ActiveStreamingPlayback;
    private playbackRequestId = 0;
    private readonly restartDelayMs = 100;

    public constructor(private accountService: AccountService) {
    }

    public startRecording(language = "en-US"): Observable<string> {
        if (this.recordingSubject && this.shouldKeepRecording) {
            return this.recordingSubject.asObservable();
        }

        const speechRecognitionConstructor = this.getSpeechRecognitionConstructor();
        if (!speechRecognitionConstructor) {
            return new Observable<string>((observer) => {
                observer.error(new Error("Speech recognition is not supported by this browser."));
            });
        }

        this.forceStopCurrentRecognition();

        this.shouldKeepRecording = true;
        this.recordingLanguage = language;
        this.committedTranscript = "";
        this.resetSessionResults();
        this.lastEmittedTranscript = "";
        this.recordingSubject = new Subject<string>();

        try {
            this.startRecognitionSession();
        } catch (error) {
            this.emitRecordingError(this.asError(error, "Unable to start speech recognition."));
        }

        return this.recordingSubject.asObservable();
    }

    public stopRecording(): void {
        this.shouldKeepRecording = false;

        if (!this.recognition) {
            this.completeRecording();
            return;
        }

        try {
            this.recognition.stop();
        } catch {
            this.completeRecording();
        }
    }

    public readText(text: string): Promise<void> {
        if (typeof window === "undefined") {
            return Promise.resolve();
        }

        const normalizedText = text.trim();
        if (!normalizedText) {
            return Promise.resolve();
        }

        const requestId = ++this.playbackRequestId;

        this.cancelActivePlayback();
        this.resolveActiveReadText();

        return new Promise<void>((resolve) => {
            this.activeReadTextResolver = resolve;
            const abortController = new AbortController();
            this.activePlaybackAbortController = abortController;

            const headers: Record<string, string> = {
                "Content-Type": "application/json",
            };
            const authToken = this.accountService.authToken;
            if (authToken) {
                headers.Authorization = `Bearer ${authToken}`;
            }

            const finish = () => {
                if (requestId !== this.playbackRequestId) {
                    return;
                }

                this.clearActivePlayback();
                if (this.activeReadTextResolver === resolve) {
                    this.activeReadTextResolver = undefined;
                }

                resolve();
            };

            const fail = (error: unknown) => {
                console.error("TTS playback failed", error);
                finish();
            };

            void fetch(`${Environment.ApiUri}/ai/tts/stream`, {
                method: "POST",
                headers,
                body: JSON.stringify({ input: normalizedText }),
                signal: abortController.signal,
            }).then(async (response) => {
                if (!response.ok) {
                    throw new Error(`TTS request failed (${response.status} ${response.statusText}).`);
                }

                await this.playStreamingResponse(response, requestId, abortController.signal, finish);
            }).catch((error: unknown) => {
                if (abortController.signal.aborted) {
                    finish();
                    return;
                }

                fail(error);
            });
        });
    }

    public interruptRead(): void {
        this.playbackRequestId++;
        this.cancelActivePlayback();
        this.resolveActiveReadText();
    }

    private cancelActivePlayback(): void {
        if (this.activePlaybackAbortController && !this.activePlaybackAbortController.signal.aborted) {
            this.activePlaybackAbortController.abort();
        }

        this.clearActivePlayback();
    }

    private resolveActiveReadText(): void {
        if (!this.activeReadTextResolver) {
            return;
        }

        const resolve = this.activeReadTextResolver;
        this.activeReadTextResolver = undefined;
        resolve();
    }

    private clearActivePlayback(): void {
        this.activePlaybackAbortController = undefined;

        const playback = this.activeStreamingPlayback;
        this.activeStreamingPlayback = undefined;

        if (playback) {
            for (const sourceNode of playback.sourceNodes) {
                sourceNode.onended = null;
                try {
                    sourceNode.stop();
                } catch {
                    // Ignore source nodes that have already completed.
                }
                sourceNode.disconnect();
            }

            playback.sourceNodes.clear();
            playback.gainNode.disconnect();
            void playback.audioContext.close().catch(() => {
                // Ignore cleanup failures when the context is already closed.
            });
        }
    }

    private async playStreamingResponse(
        response: Response,
        requestId: number,
        abortSignal: AbortSignal,
        finish: () => void,
    ): Promise<void> {
        if (!response.body) {
            throw new Error("TTS response did not include a readable stream.");
        }

        const reader = response.body.getReader();
        const chunks: Uint8Array[] = [];

        try {
            while (true) {
                const { done, value } = await reader.read();
                if (done) {
                    break;
                }

                if (requestId !== this.playbackRequestId || abortSignal.aborted) {
                    finish();
                    return;
                }

                if (value) {
                    chunks.push(value);
                }
            }
        } finally {
            reader.releaseLock();
        }

        if (requestId !== this.playbackRequestId || abortSignal.aborted) {
            finish();
            return;
        }

        const totalLength = chunks.reduce((sum, c) => sum + c.length, 0);
        if (totalLength === 0) {
            finish();
            return;
        }

        const allBytes = new Uint8Array(totalLength);
        let offset = 0;
        for (const chunk of chunks) {
            allBytes.set(chunk, offset);
            offset += chunk.length;
        }

        const audioContext = await this.createStreamingAudioContext();
        const gainNode = audioContext.createGain();
        gainNode.connect(audioContext.destination);

        this.activeStreamingPlayback = {
            audioContext,
            gainNode,
            sourceNodes: new Set<AudioBufferSourceNode>(),
            scheduledTime: audioContext.currentTime + 0.05,
            pendingSourceCount: 1,
            streamCompleted: false,
        };

        const playback = this.activeStreamingPlayback;
        const audioBuffer = await audioContext.decodeAudioData(
            allBytes.buffer.slice(allBytes.byteOffset, allBytes.byteOffset + allBytes.byteLength),
        );

        if (requestId !== this.playbackRequestId || abortSignal.aborted) {
            finish();
            return;
        }

        const sourceNode = audioContext.createBufferSource();
        sourceNode.buffer = audioBuffer;
        sourceNode.connect(gainNode);
        playback.sourceNodes.add(sourceNode);

        sourceNode.onended = () => {
            playback.sourceNodes.delete(sourceNode);
            sourceNode.disconnect();
            playback.pendingSourceCount = Math.max(0, playback.pendingSourceCount - 1);

            if (this.activeStreamingPlayback !== playback || requestId !== this.playbackRequestId) {
                return;
            }

            if (playback.streamCompleted && playback.pendingSourceCount === 0) {
                finish();
            }
        };

        const startTime = Math.max(playback.scheduledTime, audioContext.currentTime + 0.03);
        sourceNode.start(startTime);
        playback.streamCompleted = true;
    }

    private async createStreamingAudioContext(): Promise<AudioContext> {
        const browserWindow = window as BrowserWindow & {
            AudioContext?: typeof AudioContext;
            webkitAudioContext?: typeof AudioContext;
        };
        const AudioContextConstructor = browserWindow.AudioContext ?? browserWindow.webkitAudioContext;
        if (!AudioContextConstructor) {
            throw new Error("Web Audio is not supported by this browser.");
        }

        const audioContext = new AudioContextConstructor();
        if (audioContext.state === "suspended") {
            await audioContext.resume();
        }

        return audioContext;
    }

    private completeRecording(): void {
        this.recordingSubject?.complete();
        this.cleanupState();
    }

    private emitRecordingError(error: Error): void {
        this.recordingSubject?.error(error);
        this.cleanupState();
    }

    private cleanupState(): void {
        this.shouldKeepRecording = false;
        this.recordingLanguage = "en-US";
        this.committedTranscript = "";
        this.resetSessionResults();
        this.lastEmittedTranscript = "";
        this.recordingSubject = undefined;
        this.forceStopCurrentRecognition();
    }

    private forceStopCurrentRecognition(): void {
        this.clearPendingRestart();

        if (!this.recognition) {
            return;
        }

        const recognition = this.recognition;
        this.recognition = undefined;

        recognition.onresult = null;
        recognition.onerror = null;
        recognition.onend = null;

        try {
            recognition.abort();
        } catch {
            // Ignore cleanup errors from stale recognition instances.
        }
    }

    private startRecognitionSession(): void {
        const speechRecognitionConstructor = this.getSpeechRecognitionConstructor();
        if (!speechRecognitionConstructor) {
            throw new Error("Speech recognition is not supported by this browser.");
        }

        this.resetSessionResults();

        const recognition = new speechRecognitionConstructor();
        this.recognition = recognition;

        recognition.lang = "en-US";
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.maxAlternatives = 1;

        recognition.onresult = (event) => {
            if (this.recognition !== recognition) {
                return;
            }

            this.syncSessionResults(event);
            this.emitCurrentTranscript();
        };

        recognition.onerror = (event) => {
            if (this.recognition !== recognition) {
                return;
            }

            if (!this.shouldKeepRecording && event.error === "aborted") {
                return;
            }

            if (this.shouldRecoverFromError(event.error)) {
                return;
            }

            const message = event.message ? `${event.error}: ${event.message}` : event.error;
            this.emitRecordingError(new Error(`Speech recognition failed (${message}).`));
        };

        recognition.onend = () => {
            if (this.recognition !== recognition) {
                return;
            }

            this.commitCurrentSessionTranscript();
            this.recognition = undefined;

            if (!this.shouldKeepRecording) {
                this.completeRecording();
                return;
            }

            this.scheduleRecognitionRestart();
        };

        recognition.start();
    }

    private scheduleRecognitionRestart(): void {
        this.clearPendingRestart();

        this.restartTimeoutId = window.setTimeout(() => {
            this.restartTimeoutId = undefined;

            if (!this.shouldKeepRecording || this.recognition) {
                return;
            }

            try {
                this.startRecognitionSession();
            } catch (error) {
                this.emitRecordingError(this.asError(error, "Unable to restart speech recognition."));
            }
        }, this.restartDelayMs);
    }

    private clearPendingRestart(): void {
        if (this.restartTimeoutId === undefined) {
            return;
        }

        window.clearTimeout(this.restartTimeoutId);
        this.restartTimeoutId = undefined;
    }

    private syncSessionResults(event: BrowserSpeechRecognitionEvent): void {
        const { resultIndex, results } = event;

        this.sessionResults.length = results.length;

        for (let index = resultIndex; index < results.length; index++) {
            const result = results[index];
            const transcriptSegment = this.normalizeTranscript(result?.[0]?.transcript ?? "");

            this.sessionResults[index] = {
                transcript: transcriptSegment,
                isFinal: Boolean(result?.isFinal),
            };
        }
    }

    private emitCurrentTranscript(): void {
        this.emitTranscript(this.buildCombinedTranscript(true));
    }

    private emitTranscript(transcript: string): void {
        const normalizedTranscript = this.normalizeTranscript(transcript);

        if (!normalizedTranscript || normalizedTranscript === this.lastEmittedTranscript) {
            return;
        }

        this.lastEmittedTranscript = normalizedTranscript;
        this.recordingSubject?.next(normalizedTranscript);
    }

    private commitCurrentSessionTranscript(): void {
        const currentSessionTranscript = this.buildSessionTranscript(true);

        this.committedTranscript = this.mergeSessionTranscript(this.committedTranscript, currentSessionTranscript);
        this.resetSessionResults();
        this.emitTranscript(this.committedTranscript);
    }

    private shouldRecoverFromError(error: string): boolean {
        return this.shouldKeepRecording && ["aborted", "network", "no-speech"].includes(error);
    }

    private buildCombinedTranscript(includeInterim: boolean): string {
        return this.mergeSessionTranscript(this.committedTranscript, this.buildSessionTranscript(includeInterim));
    }

    private buildSessionTranscript(includeInterim: boolean): string {
        let transcript = "";

        for (const result of this.sessionResults) {
            if (!result || (!result.isFinal && !includeInterim)) {
                continue;
            }

            transcript = this.mergeSessionTranscript(transcript, result.transcript);
        }

        return this.normalizeTranscript(transcript);
    }

    private mergeSessionTranscript(baseTranscript: string, nextTranscript: string): string {
        const normalizedBaseTranscript = this.normalizeTranscript(baseTranscript);
        const normalizedNextTranscript = this.normalizeTranscript(nextTranscript);

        if (!normalizedBaseTranscript) {
            return normalizedNextTranscript;
        }

        if (!normalizedNextTranscript) {
            return normalizedBaseTranscript;
        }

        if (normalizedBaseTranscript === normalizedNextTranscript) {
            return normalizedBaseTranscript;
        }

        if (normalizedNextTranscript.startsWith(`${normalizedBaseTranscript} `)) {
            return normalizedNextTranscript;
        }

        if (normalizedBaseTranscript.endsWith(` ${normalizedNextTranscript}`)) {
            return normalizedBaseTranscript;
        }

        const baseWords = normalizedBaseTranscript.split(" ");
        const nextWords = normalizedNextTranscript.split(" ");
        const lowerCaseBaseWords = baseWords.map((word) => word.toLowerCase());
        const lowerCaseNextWords = nextWords.map((word) => word.toLowerCase());
        const maxOverlap = Math.min(baseWords.length, nextWords.length);

        for (let overlapLength = maxOverlap; overlapLength > 0; overlapLength--) {
            const baseSuffix = lowerCaseBaseWords.slice(-overlapLength).join(" ");
            const nextPrefix = lowerCaseNextWords.slice(0, overlapLength).join(" ");

            if (baseSuffix === nextPrefix) {
                return [...baseWords, ...nextWords.slice(overlapLength)].join(" ");
            }
        }

        return `${normalizedBaseTranscript} ${normalizedNextTranscript}`;
    }

    private resetSessionResults(): void {
        this.sessionResults = [];
    }

    private normalizeTranscript(transcript: string): string {
        return transcript.replace(/\s+/g, " ").trim();
    }

    private getSpeechRecognitionConstructor() {
        if (typeof window === "undefined") {
            return undefined;
        }

        const browserWindow = window as BrowserWindow;
        return browserWindow.webkitSpeechRecognition ?? browserWindow.SpeechRecognition;
    }

    private asError(error: unknown, fallbackMessage: string): Error {
        if (error instanceof Error) {
            return error;
        }

        return new Error(fallbackMessage);
    }

}
